import React, { useEffect, useState } from "react";
import DialogBox from "../../component/DialogBox";
import InputField from "../../component/InputField";
import ButtonCompo from "../../component/ButtonCompo";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AlertBox from "../../component/AlertBox";
import axios from "axios";
import { SERVER_URL } from "../../constant";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import SelectField from "../../component/SelectField";
import {
  LinearProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import PaginationBar from "../../component/PaginationBar";

function ProjectAssign() {
  const isEmployee = useSelector(
    (state) => state.employee?.isEmployeeAuthenticated
  );
  const employeeData = useSelector((state) => state.employee?.employeeData);
  const navigate = useNavigate();

  //redirect to signin if not logged in
  useEffect(() => {
    if (isEmployee !== undefined && isEmployee === false) {
      navigate("/employee/signin");
    }
  }, [isEmployee]);

  //redirect to employee dashboard if permission is not given
  useEffect(() => {
    if (employeeData !== null && employeeData?.admin?.projectAssign === false) {
      navigate("/employee/dashboard");
    }
  }, [employeeData]);

  /* ----------------------------------------------------------------------------------------------------- */
  /* ----------------------------------------Main Coding Part--------------------------------------------- */
  /* ----------------------------------------------------------------------------------------------------- */

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const [allProjects, setAllProjects] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [reload, setReload] = useState(false);
  // text hooks
  const [projectName, setProjectName] = useState("");
  const [startDate, setStartDate] = useState(dayjs());
  const [deadline, setDeadline] = useState(dayjs().add(30, "days"));
  const [description, setDescription] = useState("");
  const [clientDetails, setClientDetails] = useState("");
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState("");

  //fetch all projects
  async function getProjects() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/projects/get/all/${page}`,
        method: "get",
        withCredentials: true,
      });
      console.log(res.data?.data?.data || []);
      setAllProjects(res.data?.data?.data || []);
      setTotalPages(res.data?.data?.totalPages || 0);
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Projects fetch failed");
    } finally {
      setLoading(false);
    }
  }

  //fetch all projects
  async function getManagers() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/get/all/managers`,
        method: "get",
        withCredentials: true,
      });
      console.log(res.data?.data);
      let rawData = res.data?.data;
      let selectfieldData = [];
      rawData?.map((e) => {
        if (e?.managerRole !== "CompanySuperadmin") {
          let newObj = {
            title: `${e?.name} (${e?.role}) `, // add a extra reference for superadmin
            value: `${e?.mangerId}-${e?.managerRole}`, // we can't set object as "value" attribute of <option> tag but we can set string. I combine two data in a string.
          };
          selectfieldData.push(newObj);
        }
      });
      setManagers(selectfieldData);
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Projects fetch failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getManagers();
  }, []);

  useEffect(() => {
    getProjects();
  }, [reload, page]);

  //add project
  async function addProject() {
    if (
      projectName?.length === 0 ||
      description?.length === 0 ||
      selectedManager.length === 0
    ) {
      setOpen(true);
      setSuccess(false);
      setMessage("Please enter a project name and description and manager");
      return null;
    }
    const data = {
      name: projectName,
      startDate: startDate?.$d,
      deadline: deadline?.$d,
      clientDetails: clientDetails,
      description: description,
      managerId: selectedManager.split("-")[0],
    };
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/projects/add`,
        method: "post",
        withCredentials: true,
        data: data,
      });
      if (res.status >= 200 || res.status < 400) {
        setReload((prev) => !prev);
        setOpen(true);
        setSuccess(true);
        setMessage("Project added successfully");
      }
    } catch (error) {
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(false);
      setMessage(error?.response?.data?.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  // delete project
  async function deleteProject(projectName) {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/project/delete/${projectName}`,
        method: "delete",
        withCredentials: true,
      });
      if (res.status >= 200 || res.status < 400) {
        setReload((prev) => !prev);
        setOpen(true);
        setSuccess(true);
        setMessage("Project deleted successfully");
      }
    } catch (error) {
      setOpen(true);
      setSuccess(false);
      setMessage(error?.response?.data?.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {loading && (
        <LinearProgress className="!fixed !top-0 !left-0 !right-0 !h-[8px]" />
      )}
      <div className="w-full flex justify-center items-center flex-col">
        {success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        {!success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        <div className="w-[90vw] py-5">
          <h1 className="text-[30px] font-medium text-left w-full">
            Project Management
          </h1>
          <div className="w-full flex justify-end items-end mt-5">
            <DialogBox
              trigger={
                <ButtonCompo variant={"contained"}>Add New Project</ButtonCompo>
              }
              title="Add A New Project"
              actionButton={
                <ButtonCompo onClick={addProject}>Save</ButtonCompo>
              }
              closeActionButton="Cancel"
              maxWidth="sm"
            >
              <div className="space-y-5">
                <InputField
                  label="Project Name"
                  onChange={(e) => setProjectName(e?.target?.value)}
                  required
                />
                <SelectField
                  valueArray={managers}
                  setValue={(eve) => setSelectedManager(eve?.target?.value)}
                  label="Manager"
                />
                <DatePicker
                  value={startDate}
                  onChange={(val) => setStartDate(dayjs(val))}
                  label="Start From"
                  className="!w-full"
                />
                <DatePicker
                  value={deadline}
                  onChange={(val) => setDeadline(val)}
                  label="Deadline"
                  className="!w-full"
                />
                <InputField
                  label="Client Details"
                  onChange={(e) => setClientDetails(e?.target?.value)}
                  required
                />
                <InputField
                  label="Description"
                  onChange={(e) => setDescription(e?.target?.value)}
                  multiline
                  rows="8"
                  required
                />
              </div>
            </DialogBox>
          </div>
          <div className="w-full mt-5">
            {allProjects.length === 0 ? (
              <div>
                <p className="text-center text-[18px] text-slate-500 font-medium">
                  No project found
                </p>
              </div>
            ) : (
              <div className="shadow-lg">
                <Table>
                  <TableHead>
                    <TableRow className="!bg-blue-500">
                      <TableCell className="!text-center !text-white">
                        SL No
                      </TableCell>
                      <TableCell className="!text-center !text-white">
                        Project Name
                      </TableCell>
                      <TableCell className="!text-center !text-white">
                        Started From
                      </TableCell>
                      <TableCell className="!text-center !text-white">
                        Deadline
                      </TableCell>
                      <TableCell className="!text-center !text-white">
                        Manager
                      </TableCell>
                      <TableCell className="!text-center !text-white">
                        View
                      </TableCell>
                      <TableCell className="!text-center !text-white">
                        Delete
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allProjects.map((data, i) => (
                      <TableRow>
                        <TableCell className="!text-center">{i + 1}</TableCell>
                        <TableCell className="!text-center">
                          {data?.name}
                        </TableCell>
                        <TableCell className="!text-center">
                          {new Date(data?.startDate).toLocaleDateString(
                            "en-IN"
                          )}
                        </TableCell>
                        <TableCell className="!text-center">
                          {new Date(data?.deadline).toLocaleDateString("en-IN")}
                        </TableCell>
                        <TableCell className="!text-center">
                          {data?.manager?.fullName}
                        </TableCell>
                        <TableCell className="!text-center">
                          <Link to={`/admin/get/single/project/${data?._id}`}>
                            <ButtonCompo>View</ButtonCompo>
                          </Link>
                        </TableCell>
                        <TableCell className="!text-center">
                          <ButtonCompo color={"error"}>Delete</ButtonCompo>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <PaginationBar
                  attendancePage={page}
                  setAttendancePage={setPage}
                  totalAttendancePage={totalPages}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ProjectAssign;
